<script setup>
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from "vue";

import { useRoute } from "vue-router";
import { useCalendarStore } from "@/stores/CalendarStore";
import CalendarHeader from "@/components/CalendarHeader.vue";
import dayjs from "dayjs";
import { CurrencyPoundIcon } from "@heroicons/vue/24/solid";

// Additional date formatting featur3es
var advancedFormat = require("dayjs/plugin/advancedFormat");
dayjs.extend(advancedFormat);

const calendarStore = useCalendarStore();
const route = useRoute();
const maxVisibleWithoutMore = ref(4);
const maxVisibleWithMore = ref(3);
const gridRef = ref(null);
const cellEventsRef = ref(null);
const desktopAttentionDate = ref("");
const agendaDayRefs = new Map();
let resizeObserver;
let agendaScrollRequest = 0;
let todayAttentionRequest = 0;
let todayAttentionTimer;
let isMounted = false;

const setAgendaDayRef = (el, dateKey) => {
  if (el) {
    agendaDayRefs.set(dateKey, el);
  } else {
    agendaDayRefs.delete(dateKey);
  }
};

const isMobileAgenda = () =>
  typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(max-width: 767px)").matches;

const scrollToAgendaDay = (dateValue, behavior = "auto") => {
  if (!isMobileAgenda()) {
    return;
  }

  const dateKey = dayjs(dateValue).format("YYYY-MM-DD");
  const target = agendaDayRefs.get(dateKey);
  if (!target) {
    return;
  }

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({
    behavior: reduceMotion ? "auto" : behavior,
    block: "start",
  });
};

const queueAgendaScroll = (dateValue, behavior = "auto") => {
  const requestId = ++agendaScrollRequest;

  nextTick(() => {
    if (requestId !== agendaScrollRequest) {
      return;
    }

    scrollToAgendaDay(dateValue, behavior);
  });
};

const triggerDesktopTodayAttention = (dateValue) => {
  if (
    typeof window === "undefined"
    || typeof window.matchMedia !== "function"
    || !window.matchMedia("(min-width: 768px)").matches
  ) {
    return;
  }

  const dateKey = dayjs(dateValue).format("YYYY-MM-DD");
  const requestId = ++todayAttentionRequest;
  desktopAttentionDate.value = "";
  clearTimeout(todayAttentionTimer);

  nextTick(() => {
    if (requestId !== todayAttentionRequest) {
      return;
    }

    desktopAttentionDate.value = dateKey;
    todayAttentionTimer = setTimeout(() => {
      if (requestId === todayAttentionRequest) {
        desktopAttentionDate.value = "";
      }
    }, 900);
  });
};

const handleTodayNavigation = (dateValue) => {
  queueAgendaScroll(dateValue, "smooth");
  triggerDesktopTodayAttention(dateValue);
};

const setCellEventsRef = (el) => {
  if (el) {
    cellEventsRef.value = el;
  }
};

const updateVisibleCountFromDom = () => {
  const container =
    cellEventsRef.value
    ?? gridRef.value?.querySelector("[data-calendar-events]");
  if (!container) {
    return;
  }

  const containerHeight = (container.getBoundingClientRect().height || 0) - 2;
  if (!containerHeight) {
    return;
  }

  const sampleRow = gridRef.value?.querySelector("[data-calendar-entry]");
  const rowHeight = sampleRow?.getBoundingClientRect().height ?? 24;
  const gap = 8; // Matches space-y-2
  const moreEl = gridRef.value?.querySelector("[data-calendar-more]");
  const moreHeight = moreEl?.getBoundingClientRect().height ?? 16;
  const rowStep = rowHeight + gap;
  const countNoMore = Math.max(1, Math.floor((containerHeight + gap) / rowStep));
  const countWithMore = Math.max(
    1,
    Math.floor((containerHeight - (moreHeight + gap) + gap) / rowStep),
  );

  maxVisibleWithoutMore.value = Math.min(10, countNoMore);
  maxVisibleWithMore.value = Math.min(10, countWithMore);
};

onMounted(() => {
  calendarStore.startAutoRefresh();
  document.addEventListener("keydown", handleKeydown);
  resizeObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.target === gridRef.value) {
        updateVisibleCountFromDom();
      }
    });
  });
  if (gridRef.value) {
    resizeObserver.observe(gridRef.value);
  }
  nextTick(updateVisibleCountFromDom);
  nextTick(() => {
    isMounted = true;
  });
});

onUnmounted(() => {
  isMounted = false;
  todayAttentionRequest += 1;
  clearTimeout(todayAttentionTimer);
  calendarStore.stopAutoRefresh();
  document.removeEventListener("keydown", handleKeydown);
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

// Map events to dates and track slots
const eventsByDate = computed(() => {
  const eventsMap = {}; // Maps dates to their events
  const slotMap = new Map(); // Maps reference IDs to slots
  let slotCounter = 0; // Counter to track the slot position

  // Initialize the events map with empty arrays for each date in the range
  calendarStore.getDateRange.forEach((date) => {
    const formatted = dayjs(date).format("YYYY-MM-DD");
    eventsMap[formatted] = [];
  });

  // Iterate through each event and assign slots consistently
  calendarStore.getEntries.forEach((event) => {
    if (!event?.reference?.id || !Array.isArray(event.dates)) {
      return;
    }

    const referenceId = event.reference.id;
    let slot;
    // Use the same slot for all events with the same reference ID
    if (slotMap.has(referenceId)) {
      slot = slotMap.get(referenceId);
    } else {
      slot = slotCounter++;
      slotMap.set(referenceId, slot);
    }

    // Assign each event to its corresponding date in the map
    event.dates.forEach((eventDate) => {
      const dateStr = dayjs(eventDate.date).format("YYYY-MM-DD");
      if (eventsMap[dateStr]) {
        eventsMap[dateStr].push({ ...event, slot });
      }
    });
  });

  // Sort events on each date by slot to maintain consistent row positions
  Object.keys(eventsMap).forEach((date) => {
    eventsMap[date] = eventsMap[date].sort((a, b) => a.slot - b.slot);
  });

  return eventsMap;
});

const visibleEventsByDate = computed(() => {
  const visibleMap = {};
  const hiddenMap = {};
  Object.entries(eventsByDate.value).forEach(([date, entries]) => {
    const needsMore = entries.length > maxVisibleWithoutMore.value;
    const limit = needsMore
      ? Math.max(1, maxVisibleWithMore.value)
      : maxVisibleWithoutMore.value;
    visibleMap[date] = entries.slice(0, limit);
    hiddenMap[date] = Math.max(entries.length - limit, 0);
  });
  return { visibleMap, hiddenMap };
});

const invoiceSubtotalsByDate = computed(() => {
  const totals = {};

  calendarStore.getEntries.forEach((event) => {
    const subtotals = event?.daily_invoice_subtotals;
    if (!subtotals || typeof subtotals !== "object") {
      return;
    }

    Object.entries(subtotals).forEach(([date, amount]) => {
      totals[date] = (totals[date] ?? 0) + Number(amount ?? 0);
    });
  });

  return totals;
});

const daysForAgenda = computed(() =>
  calendarStore.getDateRange.map((date) => {
    const key = dayjs(date).format("YYYY-MM-DD");
    return {
      date,
      key,
      events: eventsByDate.value[key] || [],
      invoiceSubtotal: invoiceSubtotalsByDate.value[key],
    };
  }),
);

const expandedDate = ref("");

const toggleExpandedDate = (dateKey) => {
  expandedDate.value = expandedDate.value === dateKey ? "" : dateKey;
};

const handleKeydown = (event) => {
  if (event.key === "Escape" && expandedDate.value) {
    expandedDate.value = "";
  }
};

const getEventRoute = (event) => `/reference/${event.reference.id}`;

const getEventPrefix = (event) => event.reference?.type?.prefix ?? "";

const getEventClass = (event) => {
  const prefix = getEventPrefix(event);
  return prefix === "M1E"
    ? "label-event"
    : prefix === "M1P"
      ? "label-transport"
      : prefix === "M1S"
        ? "label-365"
        : prefix === "M1SJ"
          ? "label-365j"
          : prefix === "M1T"
            ? "label-training"
            : "label-clikable bg-teal-500 rounded-md";
};

const getEventName = (event) => event.reference?.name ?? "Untitled";

const isInvoicePaid = (event) =>
  event?.payment_status === true || event?.payment_status === 1 || event?.payment_status === "1";

const paidIconClass = "ml-auto h-4 w-4 shrink-0 rounded-full bg-white/95 p-0.5 text-emerald-700 shadow-sm ring-1 ring-white/70";
const paidIconClassLarge = "ml-auto h-5 w-5 shrink-0 rounded-full bg-white/95 p-0.5 text-emerald-700 shadow-sm ring-1 ring-white/70";

const getDayInvoiceSubtotal = (day) => {
  const key = dayjs(day).format("YYYY-MM-DD");
  const amount = invoiceSubtotalsByDate.value[key];

  return Number.isFinite(amount) ? amount : null;
};

const formatInvoiceSubtotal = (amount) => new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
  maximumFractionDigits: amount % 100 === 0 ? 0 : 2,
}).format(amount / 100);

const isToday = (day) => {
  return dayjs(day).isSame(dayjs(), "day");
};

watch(
  () => [calendarStore.getEntries, calendarStore.getDateRange],
  () => {
    nextTick(updateVisibleCountFromDom);
  },
);

watch(
  () => dayjs(calendarStore.getDate).format("YYYY-MM-DD"),
  (dateKey) => {
    if (isMounted) {
      queueAgendaScroll(dateKey, "smooth");
    }
  },
);
</script>

<template>
  <div class="space-y-4 h-full min-h-0 flex flex-col">
    <CalendarHeader
      class="w-full min-h-0"
      view="month"
      @navigate-today="handleTodayNavigation"
    ></CalendarHeader>

    <div
      ref="gridRef"
      class="grid grid-cols-7 grid-rows-[repeat(6,minmax(0,1fr))] gap-px bg-slate-200 dark:bg-slate-600 hidden md:grid flex-1 min-h-0"
    >
      <div
        v-for="(day, index) in calendarStore.getDateRange"
        :key="day"
        :data-desktop-date="dayjs(day).format('YYYY-MM-DD')"
        :class="[
          'bg-white dark:bg-slate-800 min-h-[120px] flex flex-col overflow-hidden',
          isToday(day) ? 'ring-2 ring-cyan-500/40' : '',
          desktopAttentionDate === dayjs(day).format('YYYY-MM-DD') ? 'calendar-today-pop' : '',
          (dayjs(day).format('d') == 6 || dayjs(day).format('d') == 0) ? 'bg-slate-50 dark:bg-slate-700' : '',
        ]"
      >
        <div class="flex items-center justify-between gap-2 px-3 pt-2 text-xs text-slate-500">
          <div class="flex min-w-0 items-center gap-2">
            <span class="font-medium text-slate-600 dark:text-slate-300">
            <span
              v-if="index == 0 || dayjs(day).format('M') != dayjs(calendarStore.getDateRange[index - 1]).format('M')">
              {{ dayjs(day).format("MMM") }}
            </span>
            </span>
            <span
              v-if="getDayInvoiceSubtotal(day) !== null"
              class="truncate text-[11px] font-semibold text-emerald-700 dark:text-emerald-300"
              :title="formatInvoiceSubtotal(getDayInvoiceSubtotal(day))"
            >
              {{ formatInvoiceSubtotal(getDayInvoiceSubtotal(day)) }}
            </span>
          </div>
          <div class="flex shrink-0 items-center">
            <span
              class="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-2 text-xs font-semibold"
              :class="isToday(day) ? 'bg-cyan-500 text-white' : 'text-slate-600 dark:text-slate-200'">
              {{ dayjs(day).format("D") }}
            </span>
          </div>
        </div>

        <div
          class="mt-2 flex-1 min-h-0 space-y-2 px-2 pb-2 overflow-hidden"
          :class="visibleEventsByDate.hiddenMap[dayjs(day).format('YYYY-MM-DD')] ? 'overflow-y-auto' : ''"
          :ref="index === 0 ? setCellEventsRef : null"
          data-calendar-events
        >
          <router-link
            v-for="event in visibleEventsByDate.visibleMap[dayjs(day).format('YYYY-MM-DD')]"
            :key="event.id"
            :to="getEventRoute(event)"
            class="block"
          >
            <div
              data-calendar-entry
              :class="[
                'flex items-center gap-2 rounded-md border border-transparent px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:brightness-110 hover:shadow-md dark:text-slate-100 dark:hover:brightness-110',
                getEventClass(event)
              ]"
            >
              <span class="min-w-0 flex-1 truncate" :title="getEventName(event)">{{ getEventName(event) }}</span>
              <CurrencyPoundIcon :class="paidIconClass" v-show="isInvoicePaid(event)" />
            </div>
          </router-link>

          <button
            v-if="visibleEventsByDate.hiddenMap[dayjs(day).format('YYYY-MM-DD')]"
            type="button"
            class="text-[11px] font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 text-left"
            @click="toggleExpandedDate(dayjs(day).format('YYYY-MM-DD'))"
            data-calendar-more
          >
            +{{ visibleEventsByDate.hiddenMap[dayjs(day).format('YYYY-MM-DD')] }} more
          </button>
        </div>

      </div>
    </div>

    <div class="space-y-4 md:hidden">
      <div
        v-for="day in daysForAgenda"
        :key="day.key"
        :ref="el => setAgendaDayRef(el, day.key)"
        :data-date="day.key"
        :aria-current="isToday(day.date) ? 'date' : undefined"
        :class="[
          'scroll-mt-4 rounded-xl border p-3 shadow-sm',
          isToday(day.date)
            ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-500/30 dark:border-cyan-400 dark:bg-cyan-950/40 dark:ring-cyan-400/30'
            : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800',
        ]"
      >
        <div class="flex items-center justify-between gap-3">
          <div>
            <p class="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <span>{{ dayjs(day.date).format("ddd, MMM D") }}</span>
              <span
                v-if="isToday(day.date)"
                class="rounded-full bg-cyan-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white dark:bg-cyan-400 dark:text-slate-900"
              >
                Today
              </span>
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <span
              v-if="day.invoiceSubtotal !== undefined"
              class="text-xs font-semibold text-emerald-700 dark:text-emerald-300"
            >
              {{ formatInvoiceSubtotal(day.invoiceSubtotal) }}
            </span>
            <span class="text-xs text-slate-500">{{ day.events.length }} items</span>
          </div>
        </div>
        <div v-if="!day.events.length" class="mt-2 text-xs text-slate-400">
          No entries
        </div>
        <div v-else class="mt-3 space-y-2">
          <router-link
            v-for="event in day.events"
            :key="event.id"
            :to="getEventRoute(event)"
            class="block"
          >
            <div
              :class="[
                'flex items-center gap-2 rounded-md border border-slate-200 px-2 py-2 text-xs font-medium text-slate-700 transition hover:brightness-110 hover:shadow-md dark:border-slate-700 dark:text-slate-100 dark:hover:brightness-110',
                getEventClass(event)
              ]"
            >
              <span class="min-w-0 flex-1 truncate" :title="getEventName(event)">{{ getEventName(event) }}</span>
              <CurrencyPoundIcon :class="paidIconClass" v-show="isInvoicePaid(event)" />
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <div
      v-if="expandedDate"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur p-4"
      @click.self="toggleExpandedDate(expandedDate)"
    >
      <div class="w-full max-w-2xl rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
        <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <div>
            <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {{ dayjs(expandedDate).format("ddd, MMM D") }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ (eventsByDate[expandedDate] || []).length }} items
            </p>
          </div>
          <button
            type="button"
            class="text-sm font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-300"
            @click="toggleExpandedDate(expandedDate)"
          >
            Close
          </button>
        </div>
        <div class="max-h-[70vh] overflow-y-auto p-5">
          <div v-if="!eventsByDate[expandedDate]?.length" class="text-sm text-slate-500">
            No entries
          </div>
          <div v-else class="space-y-2">
            <router-link
              v-for="event in eventsByDate[expandedDate]"
              :key="event.id"
              :to="getEventRoute(event)"
              class="block"
              @click="toggleExpandedDate(expandedDate)"
            >
              <div
                :class="[
                  'flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:brightness-110 hover:shadow-md dark:border-slate-700 dark:text-slate-100 dark:hover:brightness-110',
                  getEventClass(event)
                ]"
              >
                <span class="min-w-0 flex-1 truncate" :title="getEventName(event)">{{ getEventName(event) }}</span>
                <CurrencyPoundIcon :class="paidIconClassLarge" v-show="isInvoicePaid(event)" />
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes calendar-today-pop {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgb(6 182 212 / 0%);
  }
  38% {
    transform: scale(1.04);
    box-shadow: 0 0 0 8px rgb(6 182 212 / 28%);
  }
  70% {
    transform: scale(0.99);
    box-shadow: 0 0 0 3px rgb(6 182 212 / 18%);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgb(6 182 212 / 0%);
  }
}

.calendar-today-pop {
  position: relative;
  z-index: 10;
  animation: calendar-today-pop 850ms cubic-bezier(0.22, 1, 0.36, 1);
}

@media (prefers-reduced-motion: reduce) {
  .calendar-today-pop {
    animation: none;
    box-shadow: inset 0 0 0 3px rgb(6 182 212 / 70%);
  }
}
</style>
